import { CacheKey, CACHE_SCHEMA, CacheSchemaType } from "@/modules/cache/constants";
import { CacheClientDB } from "@/manager/cache-client/db/CacheClientDB";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { enumModule } from "@/modules/enum";

/**
 * The CacheClient exposes user-scoped reads/writes over a local disk-backed cache (Dexie).
 *
 * We support a `keepInTabMemory` option which persists reads/writes in-memory for the current tab.
 *
 * Note that the in-memory cache is NOT guaranteed to be consistent across tabs.
 */
export class CacheClient {
  db: CacheClientDB;
  private inMemoryCache = new Map<string, Map<CacheKey, unknown>>();

  private getInMemoryCache(scopeId: string) {
    let store = this.inMemoryCache.get(scopeId);

    if (!store) {
      store = new Map<CacheKey, unknown>();
      this.inMemoryCache.set(scopeId, store);
    }

    return store;
  }

  constructor({ databaseId, version = 1 }: { databaseId: string; version?: number }) {
    const db = new CacheClientDB({
      id: databaseId,
      version,
    });

    this.db = db;
  }

  initialize = async () => {
    await this.db.initialize();
  };

  recreateAfterExternalClear = async () => {
    this.inMemoryCache.clear();
    await this.db.recreateAfterExternalClear();
  };

  get cache() {
    return this.db.cacheTable();
  }

  set =
    (scopeId: string) =>
    async <TCacheKey extends CacheKey, TValue extends CacheSchemaType[TCacheKey]>(
      cacheKey: TCacheKey,
      payload: TValue
    ): Promise<boolean> => {
      const schemaId = cacheKey;
      const schema = CACHE_SCHEMA[schemaId];

      try {
        const parsedValue = schema.parse(payload);
        const serializedData = objectModule.toJson({ data: parsedValue });

        await this.cache.put({
          scopeId,
          cacheKey,
          schemaId,
          payload: serializedData,
        });

        // Invalidate in-memory entry so next read comes from disk.
        this.getInMemoryCache(scopeId).delete(cacheKey);

        return true;
      } catch (unknownErr) {
        const err = unknownErr as Error;
        const originalError = objectModule.safeErrorAsJson(err);

        const errorMessage = "[Cache.set] Failed to serialize cache data using Zod schema.";
        const errorInfo = { cacheKey, payload, originalError };
        logger.warn({ message: errorMessage, info: objectModule.safeAsJson({ errorInfo }) });

        return false;
      }
    };

  get =
    (scopeId: string) =>
    async <TCacheKey extends CacheKey, TValue extends CacheSchemaType[TCacheKey]>(
      cacheKey: TCacheKey,
      options?: { keepInTabMemory?: boolean }
    ): Promise<TValue | null> => {
      const keepInMemory = options?.keepInTabMemory === true;
      const inMemoryCache = keepInMemory ? this.getInMemoryCache(scopeId) : null;

      if (inMemoryCache?.has(cacheKey)) {
        return inMemoryCache.get(cacheKey) as TValue;
      }

      const cacheItem = await this.cache.get([scopeId, cacheKey]);

      if (!cacheItem) {
        // Even if the item is not found, we cache the null value in memory for faster subsequent reads.
        // (Writes would bust this cache).
        inMemoryCache?.set(cacheKey, null);
        return null;
      }

      const schemaId = cacheItem.schemaId;
      const targetSchemaId = enumModule.values(CacheKey).find(cacheKey => cacheKey === schemaId);
      const targetSchema = targetSchemaId && CACHE_SCHEMA[targetSchemaId];

      if (!targetSchema) {
        const errorMessage = "[Cache] Failed to parse cache schema.";
        const errorInfo = { cacheKey, cacheItem };
        logger.warn({ message: errorMessage, info: { errorInfo } });

        return null;
      }

      try {
        const jsonData = objectModule.fromJson(cacheItem.payload);
        const jsonPayload = jsonData.data;
        const payload = targetSchema.parse(jsonPayload);

        /**
         * We cast this because we know zod will enforce the correct mappings.
         */
        const typedPayload = payload as TValue;

        // After reads, we cache the value in memory for faster subsequent reads.
        inMemoryCache?.set(cacheKey, typedPayload);

        return typedPayload;
      } catch (unknownErr) {
        const err = unknownErr as Error;
        const originalError = objectModule.safeErrorAsJson(err);

        const errorMessage = "[Cache] Failed to parse cache data using Zod schema.";
        const errorInfo = { cacheKey, cacheItem, originalError };
        logger.warn({ message: errorMessage, info: { errorInfo } });

        /**
         * We clear out the bad data.
         */
        await this.del(scopeId)(cacheKey);

        return null;
      }
    };

  del =
    (scopeId: string) =>
    async <TCacheKey extends CacheKey>(cacheKey: TCacheKey): Promise<boolean> => {
      try {
        await this.cache.delete([scopeId, cacheKey]);

        // Invalidate in-memory entry
        this.getInMemoryCache(scopeId).delete(cacheKey);
        return true;
      } catch (unknownErr) {
        const err = unknownErr as Error;
        const originalError = objectModule.safeErrorAsJson(err);

        const errorMessage = "[Cache.delete] Failed to clear cached data.";
        const errorInfo = { cacheKey, originalError };
        logger.warn({ message: errorMessage, info: { errorInfo } });

        return false;
      }
    };

  clearAll = async () => {
    try {
      await this.cache.clear();
      this.inMemoryCache.clear();

      return true;
    } catch (unknownErr) {
      const err = unknownErr as Error;
      const originalError = objectModule.safeErrorAsJson(err);

      const errorMessage = "[Cache.clearAll] Failed to clear cached data.";
      const errorInfo = { originalError };
      logger.warn({ message: errorMessage, info: { errorInfo } });

      return false;
    }
  };
}
